import client from "@/lib/api";
import { shipping as validateShipping } from "@/lib/validate";

// Checkout, shared by the wizard (pay-first) and the reader (upgrade to
// hardcover). Two methods: Razorpay, which settles itself, and a plain UPI
// link, which costs no MDR but is confirmed by a human afterwards.
export const PRICES = { pdf: 399, hardcover: 799 };
// A PDF owner moving up to a hardcover pays the difference — the artwork is
// already theirs. The backend recomputes this; here it is only the label.
export const UPGRADE_PRICE = PRICES.hardcover - PRICES.pdf;

const loadCheckout = () => new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not reach the payment provider"));
    document.body.appendChild(s);
});

export async function payForBook({ bookId, kind, planKey = null, method = "razorpay", shipping = null, title = "Tales with Terry", contact = {} }) {
    // Every hardcover checkout funnels through here — the wizard, the reader's
    // cart and the upgrade dialog — so the address is checked once, before an
    // order row exists rather than after payment fails validation.
    if (kind === "hardcover") {
        const bad = validateShipping(shipping || {});
        if (bad) throw new Error(bad);
    }
    const { data } = await client.post("/orders", {
        book_id: bookId, kind, plan_key: planKey, method, shipping,
    });

    // UPI: nothing to open. The caller shows the link and collects the claim.
    if (method === "upi") return { ...data, upi: true };

    // No live keys configured — settle the stub order so dev flows end to end.
    if (!data.key_id || data.key_id.endsWith("placeholder")) {
        await client.post("/orders/verify", {
            order_id: data.id,
            razorpay_order_id: data.razorpay_order_id,
            razorpay_payment_id: `pay_stub_${Date.now()}`,
            razorpay_signature: "stub",
        });
        return { ...data, stub: true };
    }

    await loadCheckout();
    return new Promise((resolve, reject) => {
        const rz = new window.Razorpay({
            key: data.key_id,
            amount: data.amount,
            currency: data.currency,
            order_id: data.razorpay_order_id,
            name: "Tales with Terry",
            description: title,
            prefill: { name: contact.name || "", email: contact.email || "", contact: contact.phone || "" },
            theme: { color: "#FF6B9D" },
            handler: async (x) => {
                try {
                    await client.post("/orders/verify", { order_id: data.id, ...x });
                    resolve({ ...data, stub: false });
                } catch (e) { reject(e); }
            },
            modal: {
                // The webhook still settles a payment that got this far, so the
                // customer is never charged without the order catching up.
                ondismiss: () => reject(Object.assign(new Error("Payment cancelled"), { cancelled: true })),
            },
        });
        rz.open();
    });
}

// "I've paid" — parks the order in the admin's payments tab. Unlocks nothing.
export const claimUpi = (orderId, reference = "") =>
    client.post(`/orders/${orderId}/upi-claim`, { reference }).then((r) => r.data);

// "It took my money and nothing happened." Only for orders that never confirmed.
export const reportPaymentIssue = (orderId, reason = "", reference = "") =>
    client.post(`/orders/${orderId}/payment-issue`, { reason, reference }).then((r) => r.data);
